/* Recap grouping compatibility rule.
 *
 * Extracted from TradeOperationsLayout so the rule is unit-testable: importing
 * the layout pulls in the whole map/chart graph (react-leaflet ships ESM that
 * jest cannot transform), which is why this gate had no pins while it was
 * silently blocking every freight grouping.
 *
 * Helpers originally came from RecapLinkModal's validation logic.
 */

const extractBaseProduct = (productString) => {
  if (!productString) return null;
  const parts = productString.split('|').map(p => p.trim());
  return parts[0].toLowerCase();
};

const getRecapCurrency = (recap) => {
  return recap.purchase_contract_price_currency || 
         recap.sale_contract_price_currency || 
         recap.price_currency ||
         recap.currency;
};

const getRecapUnit = (recap) => {
  return recap.purchase_contract_quantity_unit || 
         recap.sale_contract_quantity_unit || 
         recap.quantity_unit ||
         (recap.isFreightRecap && recap.quantity_mt != null ? 'MT' : undefined);
};

const isFreightRow = (recap) => !!(
  recap && (recap.isFreightRecap || recap.recap_type === 'Freight')
);

const validateRecapGroupCompatibility = (allRecaps) => {
  // Freight recaps are EXEMPT from these checks. A fixture is the shipping
  // leg, not a commodity leg: its product box is usually blank and its
  // currency is the freight currency, so comparing it against the trade legs
  // produced a "Product Mismatch" modal that blocked every attempt to group a
  // freight recap with its own sale/purchase recaps (live bug, fixed
  // 2026-08-17). Trade-vs-trade compatibility is unchanged.
  const recaps = (allRecaps || []).filter((r) => !isFreightRow(r));
  if (recaps.length < 2) {
    return { valid: true };
  }

  const firstRecap = recaps[0];
  const firstProduct = extractBaseProduct(firstRecap.product);
  const firstCurrency = getRecapCurrency(firstRecap);
  const firstUnit = getRecapUnit(firstRecap);
  
  const errors = [];

  // Check each recap against the first one
  for (let i = 1; i < recaps.length; i++) {
    const recap = recaps[i];
    const recapProduct = extractBaseProduct(recap.product);
    const recapCurrency = getRecapCurrency(recap);
    const recapUnit = getRecapUnit(recap);

    // Check product mismatch
    if (recapProduct !== firstProduct) {
      errors.push({
        recapId: recap.id,
        recap: recap,
        reason: 'Product Mismatch',
        details: `Expected: ${firstProduct || 'N/A'}, Got: ${recapProduct || 'N/A'}`
      });
      continue; // Skip other checks if product doesn't match
    }

    // Check currency mismatch
    if (recapCurrency !== firstCurrency) {
      errors.push({
        recapId: recap.id,
        recap: recap,
        reason: 'Currency Mismatch',
        details: `Expected: ${firstCurrency || 'N/A'}, Got: ${recapCurrency || 'N/A'}`
      });
      continue;
    }

    // Check unit mismatch
    if (recapUnit !== firstUnit) {
      errors.push({
        recapId: recap.id,
        recap: recap,
        reason: 'Unit Mismatch',
        details: `Expected: ${firstUnit || 'N/A'}, Got: ${recapUnit || 'N/A'}`
      });
    }
  }

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  return { valid: true };
};

export {
  extractBaseProduct,
  getRecapCurrency,
  getRecapUnit,
  isFreightRow,
  validateRecapGroupCompatibility,
};
